import type {
  AdminPaymentOrder,
  AdminPaymentOrderDetail,
  AppConfig,
  AppRecord,
  CredentialMetadata,
  PayChannelConfig,
  PaymentConfig,
  PaymentReconciliationDifference,
  PaymentDifferenceAudit,
  PaymentRefund,
  PaymentStatementBatch,
  PaymentStatementJob,
  PaymentStatementImportInput,
  PaymentStatementRowInput,
  PaymentWebhookEvent,
  PaymentWebhookEventAudit,
  WechatLoginSettings,
} from "@yahshi/contracts";
import {
  upsertWechatLoginSettingsSchema,
  drivingProfileOptionsSchema,
  appConfigSchema,
  createAppInputSchema,
  paymentStatementImportInputSchema,
  triggerPaymentStatementJobInputSchema,
  updatePaymentDifferenceInputSchema,
  updateAppInputSchema,
  upsertPayChannelInputSchema,
} from "@yahshi/contracts";
import type { AppListFilters, ConfigUpsertInput, M1AppApi } from "./M1AppApi";
import { M1ApiError } from "./M1AppApi";

type DevelopmentState = {
  apps: AppRecord[];
  credentials: Record<string, CredentialMetadata>;
  configs: Record<string, AppConfig[]>;
  payChannels: Record<string, PayChannelConfig[]>;
  paymentConfigs: PaymentConfig[];
  wechatLoginSettings?: Record<string, WechatLoginSettings>;
};

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;

const STORAGE_KEY = "yahshi-manage:m1-dev-state:v1";

const seedState: DevelopmentState = {
  apps: [
    {
      id: "app_yahshi_web",
      name: "Yahshi Web",
      code: "yahshi-web",
      description: "Yahshi 主站与公开 Web 服务",
      status: "active",
      platforms: ["web", "mini-program", "android", "ios"],
      payChannels: ["wxpay", "alipay"],
      configCount: 3,
      updatedAt: "2026-09-02T02:18:00.000Z",
    },
    {
      id: "app_yahshi_admin",
      name: "Yahshi Admin",
      code: "yahshi-admin",
      description: "内部运营管理控制台",
      status: "active",
      platforms: ["web"],
      payChannels: [],
      configCount: 2,
      updatedAt: "2026-09-01T09:35:00.000Z",
    },
    {
      id: "app_partner_sandbox",
      name: "合作方沙箱",
      code: "partner-sandbox",
      description: "外部合作方联调环境",
      status: "disabled",
      platforms: ["web", "android", "ios"],
      payChannels: ["wxpay"],
      configCount: 1,
      updatedAt: "2026-08-28T06:42:00.000Z",
    },
  ],
  credentials: {
    app_yahshi_web: { appKey: "yk_live_web_2c7c91f4", secretLastResetAt: "2026-08-12T03:20:00.000Z" },
    app_yahshi_admin: { appKey: "yk_live_admin_947ea461", secretLastResetAt: "2026-08-20T08:10:00.000Z" },
    app_partner_sandbox: { appKey: "yk_test_partner_86f0b123", secretLastResetAt: "2026-08-28T06:42:00.000Z" },
  },
  configs: {
    app_yahshi_web: [
      { key: "API_BASE_URL", value: "https://api.yahshi.cn", valueType: "string", isPublic: true, description: "公开 API 地址" },
      { key: "MAINTENANCE_MODE", value: "false", valueType: "boolean", isPublic: true, description: "维护模式开关" },
      { key: "INTERNAL_BATCH_SIZE", value: "200", valueType: "number", isPublic: false, description: "内部批处理大小" },
    ],
    app_yahshi_admin: [
      { key: "API_BASE_URL", value: "https://api.yahshi.cn", valueType: "string", isPublic: false, description: "管理 API 地址" },
      { key: "SESSION_IDLE_MINUTES", value: "30", valueType: "number", isPublic: false, description: "会话空闲时间" },
    ],
    app_partner_sandbox: [
      { key: "SANDBOX", value: "true", valueType: "boolean", isPublic: true, description: "沙箱标记" },
    ],
  },
  payChannels: {
    app_yahshi_web: [
      { channel: "wxpay", enabled: true, accountId: "190000****", secretRef: "WXPAY_API_V3_KEY", description: "微信支付主商户", updatedAt: "2026-08-30T01:10:00.000Z" },
      { channel: "alipay", enabled: false, accountId: "2021000****", secretRef: "ALIPAY_PRIVATE_KEY", description: "支付宝渠道，待启用", updatedAt: "2026-08-26T05:25:00.000Z" },
    ],
    app_yahshi_admin: [],
    app_partner_sandbox: [
      { channel: "wxpay", enabled: false, accountId: "sandbox-1900", secretRef: "SANDBOX_WXPAY_KEY", description: "沙箱商户", updatedAt: "2026-08-28T06:42:00.000Z" },
    ],
  },
  paymentConfigs: [
    { id: "payment-config-wx-main", name: "微信支付主商户", channel: "wxpay", scenarios: ["wx_native", "wx_jsapi", "wx_mini_program", "wx_h5", "wx_bill"], accountId: "190000****", secretRef: "WXPAY_API_V3_KEY", credentialRefs: { appId: "WXPAY_APP_ID", merchantSerial: "WXPAY_MERCHANT_SERIAL", merchantCertificate: "WXPAY_MERCHANT_CERTIFICATE", privateKey: "WXPAY_PRIVATE_KEY", apiV3Key: "WXPAY_API_V3_KEY", platformPublicKeyId: "WXPAY_PLATFORM_PUBLIC_KEY_ID", platformPublicKey: "WXPAY_PLATFORM_PUBLIC_KEY" }, credentialStatus: { appId: true, merchantSerial: true, merchantCertificate: true, privateKey: true, apiV3Key: true, platformPublicKeyId: true, platformPublicKey: true }, protocolConfig: { wxpayMerchantType: "ordinary", wxpayVerificationMode: "public_key" }, notifyUrl: "https://api.yahshi.cn/webhooks/wechat-pay", description: "主站统一微信支付配置。", enabled: true, updatedAt: "2026-09-10T03:00:00.000Z", associatedApps: [{ id: "app_yahshi_web", name: "Yahshi Web", code: "yahshi-web" }] },
    { id: "payment-config-alipay-main", name: "支付宝生产应用", channel: "alipay", scenarios: ["alipay_page", "alipay_wap", "alipay_app"], accountId: "2021000****", secretRef: "ALIPAY_PRIVATE_KEY", credentialRefs: { privateKey: "ALIPAY_PRIVATE_KEY", appCert: "ALIPAY_APP_CERT", alipayPublicCert: "ALIPAY_PUBLIC_CERT", rootCert: "ALIPAY_ROOT_CERT" }, credentialStatus: { privateKey: true, appCert: true, alipayPublicCert: true, rootCert: true }, protocolConfig: { alipaySignAlgorithm: "RSA2", alipayKeyMode: "certificate" }, notifyUrl: "https://api.yahshi.cn/webhooks/alipay", description: "支付宝生产应用配置。", enabled: false, updatedAt: "2026-09-09T08:30:00.000Z", associatedApps: [] },
  ],
};

const developmentPaymentOrders: AdminPaymentOrder[] = [
  {
    id: "ord_demo_20260910001", appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web",
    userId: "usr_demo_1001", userPublicId: "100001", userName: "林夏", channel: "wxpay",
    amountMinor: 1990, currency: "CNY", description: "月度会员", status: "succeeded",
    providerOrderId: "wx_demo_20260910001", failureCode: null, failureReason: null,
    attemptStatus: "ready", attemptExpiresAt: "2026-09-10T11:32:00.000Z", latestTradeState: "SUCCESS",
    latestNotificationStatus: "processed", latestRejectionReason: null,
    createdAt: "2026-09-10T11:17:00.000Z", updatedAt: "2026-09-10T11:18:00.000Z", paidAt: "2026-09-10T11:18:00.000Z",
  },
  {
    id: "ord_demo_20260910002", appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web",
    userId: "usr_demo_1002", userPublicId: "100002", userName: "陈默", channel: "wxpay",
    amountMinor: 600, currency: "CNY", description: "内容解锁", status: "processing",
    providerOrderId: null, failureCode: null, failureReason: null,
    attemptStatus: "ready", attemptExpiresAt: "2026-09-10T12:08:00.000Z", latestTradeState: "NOTPAY",
    latestNotificationStatus: "ignored", latestRejectionReason: null,
    createdAt: "2026-09-10T11:52:00.000Z", updatedAt: "2026-09-10T11:53:00.000Z", paidAt: null,
  },
  {
    id: "ord_demo_20260910003", appId: "app_partner_sandbox", appName: "合作方沙箱", appCode: "partner-sandbox",
    userId: "usr_demo_1003", userPublicId: "100003", userName: "测试用户 03", channel: "wxpay",
    amountMinor: 1, currency: "CNY", description: "支付联调", status: "failed",
    providerOrderId: null, failureCode: "WECHAT_PAYERROR", failureReason: "微信支付返回支付失败",
    attemptStatus: "failed", attemptExpiresAt: null, latestTradeState: "PAYERROR",
    latestNotificationStatus: "processed", latestRejectionReason: null,
    createdAt: "2026-09-09T08:20:00.000Z", updatedAt: "2026-09-09T08:21:00.000Z", paidAt: null,
  },
  {
    id: "ord_demo_20260909004", appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web",
    userId: "usr_demo_1004", userPublicId: "100004", userName: "周然", channel: "alipay",
    amountMinor: 12800, currency: "CNY", description: "年度会员", status: "canceled",
    providerOrderId: null, failureCode: null, failureReason: null,
    attemptStatus: null, attemptExpiresAt: null, latestTradeState: null,
    latestNotificationStatus: null, latestRejectionReason: null,
    createdAt: "2026-09-08T05:20:00.000Z", updatedAt: "2026-09-08T05:25:00.000Z", paidAt: null,
  },
];

const developmentPaymentRefunds: Record<string, PaymentRefund[]> = {
  ord_demo_20260910001: [{
    id: "rfd_demo_20260910001", orderId: "ord_demo_20260910001", appId: "app_yahshi_web", idempotencyKey: "refund_demo_20260910001",
    outRefundNo: "rfd_demo_20260910001", providerRefundId: "wx_refund_demo_001", amountMinor: 500, currency: "CNY",
    reason: "用户申请退款", status: "succeeded", failureReason: null, version: 2,
    createdAt: "2026-09-10T12:00:00.000Z", updatedAt: "2026-09-10T12:01:00.000Z", completedAt: "2026-09-10T12:01:00.000Z",
  }],
};

const seedStatementBatches: PaymentStatementBatch[] = [{
  id: "stm_demo_20260912_wxpay",
  channel: "wxpay",
  fileName: "微信支付账单_20260906_20260912.csv",
  checksum: "a".repeat(64),
  dateFrom: "2026-09-06",
  dateTo: "2026-09-12",
  status: "attention",
  rowCount: 2,
  matchedCount: 1,
  mismatchCount: 1,
  grossAmountMinor: 5_890,
  refundedAmountMinor: 500,
  errorMessage: null,
  createdBy: "local-admin",
  createdAt: "2026-09-12T08:20:00.000Z",
  completedAt: "2026-09-12T08:20:02.000Z",
}];

const seedStatementDifferences: PaymentReconciliationDifference[] = [{
  id: "dif_demo_missing_platform",
  batchId: "stm_demo_20260912_wxpay",
  channel: "wxpay",
  type: "missing_platform",
  merchantOrderId: "ord_channel_only_001",
  providerOrderId: "wx_channel_only_001",
  platformAmountMinor: null,
  providerAmountMinor: 3_900,
  platformRefundedAmountMinor: null,
  providerRefundedAmountMinor: 0,
  platformStatus: null,
  providerStatus: "succeeded",
  resolutionStatus: "open",
  assignee: "支付运营组",
  resolutionAction: "none",
  resolutionNote: null,
  resolvedAt: null,
  updatedAt: "2026-09-12T08:20:02.000Z",
  createdAt: "2026-09-12T08:20:02.000Z",
}];

const seedStatementJobs: PaymentStatementJob[] = [{
  id: "stj_demo_20260912_wxpay",
  configId: "payment-config-wx-main",
  configName: "微信支付主商户",
  channel: "wxpay",
  statementDate: "2026-09-12",
  trigger: "scheduled",
  status: "completed",
  attemptCount: 1,
  batchId: "stm_demo_20260912_wxpay",
  errorMessage: null,
  startedAt: "2026-09-12T08:19:58.000Z",
  completedAt: "2026-09-12T08:20:02.000Z",
  createdBy: "statement-scheduler",
  createdAt: "2026-09-12T08:19:57.000Z",
}];

const seedDifferenceAudits: PaymentDifferenceAudit[] = [{
  id: "dra_demo_created",
  differenceId: "dif_demo_missing_platform",
  action: "created",
  fromStatus: null,
  toStatus: "open",
  assignee: "支付运营组",
  resolutionAction: "none",
  note: "自动对账发现渠道单边订单",
  operatorId: "statement-worker",
  createdAt: "2026-09-12T08:20:02.000Z",
}];

const seedWebhookEvents: PaymentWebhookEvent[] = [
  {
    id: "pwe_demo_wx_success", eventKey: "wxpay-event-demo-001", channel: "wxpay", eventType: "payment", source: "callback",
    providerEventId: "wx-event-demo-001", orderId: "ord_demo_20260910001", refundId: null, outTradeNo: "ord_demo_20260910001",
    outRefundNo: null, providerOrderId: "wx_demo_20260910001", tradeState: "SUCCESS", normalizedState: "succeeded",
    amountMinor: 1990, currency: "CNY", signatureStatus: "verified", signatureSerial: "777F8E3C-demo",
    payloadDigest: "1".repeat(64), processingStatus: "processed", failureCode: null, failureReason: null,
    duplicateCount: 2, attemptCount: 1, requestTimestamp: "2026-09-12T08:15:00.000Z", occurredAt: "2026-09-12T08:15:01.000Z",
    createdAt: "2026-09-12T08:15:00.000Z", updatedAt: "2026-09-12T08:15:01.000Z", processedAt: "2026-09-12T08:15:01.000Z",
  },
  {
    id: "pwe_demo_alipay_failed", eventKey: "alipay:event-demo-002", channel: "alipay", eventType: "payment", source: "callback",
    providerEventId: "alipay-event-demo-002", orderId: null, refundId: null, outTradeNo: "ord_demo_20260909004",
    outRefundNo: null, providerOrderId: "ali_demo_002", tradeState: "TRADE_SUCCESS", normalizedState: "succeeded",
    amountMinor: 12800, currency: "CNY", signatureStatus: "verified", signatureSerial: null,
    payloadDigest: "2".repeat(64), processingStatus: "failed", failureCode: "PROCESSING_ERROR", failureReason: "关联渠道配置暂未启用",
    duplicateCount: 0, attemptCount: 1, requestTimestamp: "2026-09-12T07:42:00.000Z", occurredAt: "2026-09-12T07:41:58.000Z",
    createdAt: "2026-09-12T07:42:00.000Z", updatedAt: "2026-09-12T07:42:01.000Z", processedAt: "2026-09-12T07:42:01.000Z",
  },
  {
    id: "pwe_demo_signature_rejected", eventKey: `rejected:${"3".repeat(64)}`, channel: "wxpay", eventType: "payment", source: "callback",
    providerEventId: null, orderId: null, refundId: null, outTradeNo: null, outRefundNo: null, providerOrderId: null,
    tradeState: "UNVERIFIED", normalizedState: "pending", amountMinor: null, currency: null, signatureStatus: "rejected", signatureSerial: null,
    payloadDigest: "3".repeat(64), processingStatus: "rejected", failureCode: "SIGNATURE_REJECTED", failureReason: "微信支付通知验签失败",
    duplicateCount: 0, attemptCount: 1, requestTimestamp: null, occurredAt: null,
    createdAt: "2026-09-12T06:20:00.000Z", updatedAt: "2026-09-12T06:20:00.000Z", processedAt: "2026-09-12T06:20:00.000Z",
  },
  {
    id: "pwe_demo_refund_success", eventKey: "wxpay-refund-event-demo-004", channel: "wxpay", eventType: "refund", source: "callback",
    providerEventId: "wx-refund-event-demo-004", orderId: "ord_demo_20260910001", refundId: "rfd_demo_20260910001",
    outTradeNo: "ord_demo_20260910001", outRefundNo: "rfd_demo_20260910001", providerOrderId: "wx_refund_demo_001",
    tradeState: "SUCCESS", normalizedState: "succeeded", amountMinor: 500, currency: "CNY", signatureStatus: "verified",
    signatureSerial: "777F8E3C-demo", payloadDigest: "4".repeat(64), processingStatus: "processed", failureCode: null, failureReason: null,
    duplicateCount: 0, attemptCount: 1, requestTimestamp: "2026-09-12T05:10:00.000Z", occurredAt: "2026-09-12T05:10:02.000Z",
    createdAt: "2026-09-12T05:10:00.000Z", updatedAt: "2026-09-12T05:10:02.000Z", processedAt: "2026-09-12T05:10:02.000Z",
  },
];

const seedWebhookAudits: PaymentWebhookEventAudit[] = seedWebhookEvents.flatMap((event) => [
  {
    id: `pwa_${event.id}_received`, eventId: event.id,
    action: event.signatureStatus === "verified" ? "received" : "signature_rejected",
    fromStatus: null, toStatus: event.signatureStatus === "verified" ? "received" : "rejected",
    reason: event.signatureStatus === "verified" ? null : event.failureReason, operatorId: `webhook:${event.channel}`, createdAt: event.createdAt,
  },
  ...(event.signatureStatus === "verified" ? [{
    id: `pwa_${event.id}_result`, eventId: event.id, action: event.processingStatus as "processed" | "failed",
    fromStatus: "received" as const, toStatus: event.processingStatus, reason: event.failureReason,
    operatorId: `webhook:${event.channel}`, createdAt: event.updatedAt,
  }] : []),
]);

function developmentPaymentDetail(order: AdminPaymentOrder): AdminPaymentOrderDetail {
  const { attemptStatus, attemptExpiresAt, latestTradeState, latestNotificationStatus, latestRejectionReason, ...base } = order;
  return {
    order: { ...base, metadata: { source: "development", product: order.description }, version: order.status === "pending" ? 1 : 2 },
    attempt: attemptStatus ? {
      status: attemptStatus,
      hasCodeUrl: attemptStatus === "ready",
      expiresAt: attemptExpiresAt,
      providerErrorCode: order.failureCode,
      providerErrorReason: order.failureReason,
      createdAt: order.createdAt,
      updatedAt: order.updatedAt,
    } : null,
    notifications: latestTradeState && latestNotificationStatus ? [{
      id: `pnt_${order.id}`,
      source: "query",
      tradeState: latestTradeState,
      tradeType: order.channel === "wxpay" ? "NATIVE" : null,
      amountMinor: order.amountMinor,
      currency: order.currency,
      status: latestNotificationStatus,
      rejectionReason: latestRejectionReason,
      occurredAt: order.updatedAt,
      createdAt: order.updatedAt,
      processedAt: order.updatedAt,
    }] : [],
    refunds: structuredClone(developmentPaymentRefunds[order.id] ?? []),
  };
}

function cloneState(value: DevelopmentState): DevelopmentState {
  return structuredClone(value);
}

function loadState(): DevelopmentState {
  if (typeof window === "undefined") return cloneState(seedState);
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (!stored) return cloneState(seedState);
  try {
    const parsed = JSON.parse(stored) as DevelopmentState;
    return {
      ...parsed,
      apps: parsed.apps.map((app) => ({ ...app, platforms: app.platforms?.length ? app.platforms : ["web"] })),
      paymentConfigs: parsed.paymentConfigs ?? cloneState(seedState).paymentConfigs,
    };
  } catch {
    return cloneState(seedState);
  }
}

function createOpaqueValue(prefix: string, bytes = 24): string {
  const values = new Uint8Array(bytes);
  globalThis.crypto.getRandomValues(values);
  const value = Array.from(values, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${prefix}_${value}`;
}

function validateConfigValue(input: ConfigUpsertInput): void {
  try {
    if (input.valueType === "number" && (!Number.isFinite(Number(input.value)) || input.value.trim() === "")) {
      throw new Error("配置值必须是有效数字");
    }
    if (input.valueType === "boolean" && input.value !== "true" && input.value !== "false") {
      throw new Error("布尔配置只能填写 true 或 false");
    }
    if (input.valueType === "json") JSON.parse(input.value);
  } catch (error) {
    const message = error instanceof Error && error.message.startsWith("配置")
      ? error.message
      : "配置值必须是有效 JSON";
    throw new M1ApiError("INVALID_CONFIG_VALUE", message, { value: message });
  }
}

export function createDevelopmentM1AppApi(): M1AppApi {
  let state = loadState();
  let statementBatches = structuredClone(seedStatementBatches);
  let statementDifferences = structuredClone(seedStatementDifferences);
  let statementJobs = structuredClone(seedStatementJobs);
  let differenceAudits = structuredClone(seedDifferenceAudits);
  let webhookEvents = structuredClone(seedWebhookEvents);
  let webhookAudits = structuredClone(seedWebhookAudits);
  const statementRows = new Map<string, PaymentStatementRowInput[]>();

  const reconcileStatement = (input: PaymentStatementImportInput, batchId: string, createdAt: string): PaymentStatementBatch => {
    const differences: PaymentReconciliationDifference[] = [];
    const platformOrders = developmentPaymentOrders.filter((order) =>
      order.channel === input.channel
      && order.createdAt.slice(0, 10) >= input.dateFrom
      && order.createdAt.slice(0, 10) <= input.dateTo
      && (order.status === "succeeded" || order.status === "refunded"));
    const providerIds = new Set(input.rows.map((row) => row.merchantOrderId));
    let matchedCount = 0;
    const addDifference = (row: PaymentStatementRowInput | undefined, order: AdminPaymentOrder | undefined, type: PaymentReconciliationDifference["type"]) => {
      differences.push({
        id: `dif_${batchId}_${differences.length + 1}`,
        batchId,
        channel: input.channel,
        type,
        merchantOrderId: row?.merchantOrderId ?? order?.id ?? null,
        providerOrderId: row?.providerOrderId ?? order?.providerOrderId ?? null,
        platformAmountMinor: order?.amountMinor ?? null,
        providerAmountMinor: row?.amountMinor ?? null,
        platformRefundedAmountMinor: order ? developmentPaymentDetail(order).refunds.filter((refund) => refund.status === "processing" || refund.status === "succeeded").reduce((sum, refund) => sum + refund.amountMinor, 0) : null,
        providerRefundedAmountMinor: row?.refundedAmountMinor ?? null,
        platformStatus: order ? (order.status === "refunded" || developmentPaymentDetail(order).refunds.some((refund) => refund.status === "processing" || refund.status === "succeeded") ? "refunded" : "succeeded") : null,
        providerStatus: row?.status ?? null,
        resolutionStatus: "open",
        assignee: null,
        resolutionAction: "none",
        resolutionNote: null,
        resolvedAt: null,
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      });
    };
    input.rows.forEach((row) => {
      const order = platformOrders.find((item) => item.id === row.merchantOrderId);
      if (!order) return addDifference(row, undefined, "missing_platform");
      const refunds = developmentPaymentDetail(order).refunds.filter((refund) => refund.status === "processing" || refund.status === "succeeded").reduce((sum, refund) => sum + refund.amountMinor, 0);
      const platformStatus = order.status === "refunded" || refunds > 0 ? "refunded" : "succeeded";
      if (order.amountMinor !== row.amountMinor) return addDifference(row, order, "amount_mismatch");
      if (refunds !== row.refundedAmountMinor) return addDifference(row, order, "refund_mismatch");
      if (platformStatus !== row.status) return addDifference(row, order, "status_mismatch");
      matchedCount += 1;
    });
    platformOrders.filter((order) => !providerIds.has(order.id)).forEach((order) => addDifference(undefined, order, "missing_provider"));
    statementDifferences = [...differences, ...statementDifferences.filter((item) => item.batchId !== batchId)];
    const batch: PaymentStatementBatch = {
      id: batchId,
      channel: input.channel,
      fileName: input.fileName,
      checksum: input.checksum,
      dateFrom: input.dateFrom,
      dateTo: input.dateTo,
      status: differences.length ? "attention" : "completed",
      rowCount: input.rows.length,
      matchedCount,
      mismatchCount: differences.length,
      grossAmountMinor: input.rows.reduce((sum, row) => sum + row.amountMinor, 0),
      refundedAmountMinor: input.rows.reduce((sum, row) => sum + row.refundedAmountMinor, 0),
      errorMessage: null,
      createdBy: "local-admin",
      createdAt,
      completedAt: new Date().toISOString(),
    };
    statementBatches = [batch, ...statementBatches.filter((item) => item.id !== batchId)];
    return batch;
  };

  const persist = () => {
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  };

  const requireApp = (appId: string) => {
    const app = state.apps.find((item) => item.id === appId);
    if (!app) throw new M1ApiError("APP_NOT_FOUND", "应用不存在或已被移除");
    return app;
  };

  return {
    async listApps(filters: AppListFilters) {
      const keyword = filters.keyword.trim().toLocaleLowerCase();
      const filtered = state.apps.filter((app) => {
        const matchesStatus = filters.status === "all" || app.status === filters.status;
        const matchesKeyword = !keyword || `${app.name} ${app.code} ${app.description}`.toLocaleLowerCase().includes(keyword);
        return matchesStatus && matchesKeyword;
      }).sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
      const page = filters.page ?? DEFAULT_PAGE;
      const pageSize = filters.pageSize ?? DEFAULT_PAGE_SIZE;
      const offset = (page - 1) * pageSize;
      return {
        items: structuredClone(filtered.slice(offset, offset + pageSize)),
        total: filtered.length,
        activeTotal: filtered.filter((app) => app.status === "active").length,
      };
    },
    async getApp(appId) {
      return structuredClone(requireApp(appId));
    },
    async getWechatLoginSettings(appId) {
      requireApp(appId);
      return structuredClone(state.wechatLoginSettings?.[appId] ?? { appId, wxAppId: null, enabled: false, maxAttemptsPerMinute: 60, secretSource: "none", secretConfigured: false, appIdLocked: false, version: null, updatedAt: null });
    },
    async upsertWechatLoginSettings(appId, input) {
      const app = requireApp(appId);
      const parsed = upsertWechatLoginSettingsSchema.parse(input);
      const existing = state.wechatLoginSettings?.[appId];
      if (!app.platforms.includes("mini-program")) throw new M1ApiError("WECHAT_PLATFORM_REQUIRED", "请先在基本信息中启用小程序平台");
      if (parsed.expectedVersion !== (existing?.version ?? null)) throw new M1ApiError("WECHAT_SETTINGS_CONFLICT", "微信配置已被更新，请刷新后重新保存");
      if (existing?.appIdLocked && existing.wxAppId !== parsed.wxAppId) throw new M1ApiError("WECHAT_APP_ID_LOCKED", "该应用已使用微信登录，不能更换微信 AppID");
      if (!parsed.appSecret && (!existing || existing.wxAppId !== parsed.wxAppId)) throw new M1ApiError("WECHAT_SECRET_REQUIRED", "请输入对应的 AppSecret");
      // The demo stores metadata only; never persist the submitted secret in browser storage.
      const configs = state.configs[appId] ?? [];
      const currentConfig = configs.find((item) => item.key === "WECHAT_LOGIN_CONFIG");
      let previousRef = "";
      if (currentConfig) {
        try {
          const parsedConfig = JSON.parse(currentConfig.value) as { appSecretRef?: unknown };
          if (typeof parsedConfig.appSecretRef === "string") previousRef = parsedConfig.appSecretRef;
        } catch { /* The next save replaces an unreadable config with a new reference. */ }
      }
      const appSecretRef = parsed.appSecret || !previousRef ? `stored://wechat/${crypto.randomUUID()}` : previousRef;
      const wechatConfig: AppConfig = {
        key: "WECHAT_LOGIN_CONFIG",
        value: JSON.stringify({ wxAppId: parsed.wxAppId, appSecretRef, enabled: parsed.enabled, maxAttemptsPerMinute: parsed.maxAttemptsPerMinute }),
        valueType: "json",
        isPublic: false,
        description: "微信小程序登录",
      };
      const nextConfigs = currentConfig ? configs.map((item) => item.key === "WECHAT_LOGIN_CONFIG" ? wechatConfig : item) : [...configs, wechatConfig];
      const saved: WechatLoginSettings = { appId, wxAppId: parsed.wxAppId, enabled: parsed.enabled, maxAttemptsPerMinute: parsed.maxAttemptsPerMinute, secretSource: "stored", secretConfigured: true, appIdLocked: existing?.appIdLocked ?? false, version: crypto.randomUUID().replaceAll("-", ""), updatedAt: new Date().toISOString() };
      state = {
        ...state,
        wechatLoginSettings: { ...state.wechatLoginSettings, [appId]: saved },
        configs: { ...state.configs, [appId]: nextConfigs },
        apps: state.apps.map((item) => item.id === appId ? { ...item, configCount: nextConfigs.length } : item),
      };
      persist();
      return structuredClone(saved);
    },
    async createApp(input) {
      const parsed = createAppInputSchema.safeParse(input);
      if (!parsed.success) throw new M1ApiError("VALIDATION_ERROR", "请检查应用信息", { code: "应用标识格式不正确" });
      if (state.apps.some((item) => item.code === parsed.data.code)) {
        throw new M1ApiError("APP_CODE_CONFLICT", "应用标识已存在，请更换后重试", { code: "应用标识已存在" });
      }
      const now = new Date().toISOString();
      const id = `app_${parsed.data.code.replaceAll("-", "_")}_${Date.now().toString(36)}`;
      const app: AppRecord = { ...parsed.data, id, status: "active", payChannels: [], configCount: 0, updatedAt: now };
      const appKey = createOpaqueValue("yk", 10);
      const appSecret = createOpaqueValue("ys", 32);
      state = {
        ...state,
        apps: [app, ...state.apps],
        credentials: { ...state.credentials, [id]: { appKey, secretLastResetAt: now } },
        configs: { ...state.configs, [id]: [] },
        payChannels: { ...state.payChannels, [id]: [] },
      };
      persist();
      return { app: structuredClone(app), credential: { appKey, appSecret, revealedAt: now } };
    },
    async updateApp(appId, input) {
      const current = requireApp(appId);
      const parsed = updateAppInputSchema.safeParse(input);
      if (!parsed.success) throw new M1ApiError("VALIDATION_ERROR", "请检查应用信息");
      if (parsed.data.expectedUpdatedAt !== current.updatedAt) throw new M1ApiError("APP_VERSION_CONFLICT", "应用已更新，请加载最新信息后重新编辑");
      if (parsed.data.code && state.apps.some((item) => item.id !== appId && item.code === parsed.data.code)) {
        throw new M1ApiError("APP_CODE_CONFLICT", "应用标识已存在，请更换后重试", { code: "应用标识已存在" });
      }
      const updatedAt = new Date(Math.max(Date.now(), Date.parse(current.updatedAt) + 1)).toISOString();
      state = {
        ...state,
        apps: state.apps.map((item) => {
          if (item.id !== appId) return item;
          return {
            ...item,
            ...(parsed.data.name !== undefined ? { name: parsed.data.name } : {}),
            ...(parsed.data.code !== undefined ? { code: parsed.data.code } : {}),
            ...(parsed.data.description !== undefined ? { description: parsed.data.description } : {}),
            ...(parsed.data.status !== undefined ? { status: parsed.data.status } : {}),
            ...(parsed.data.platforms !== undefined ? { platforms: parsed.data.platforms } : {}),
            updatedAt,
          };
        }),
      };
      persist();
      return structuredClone(requireApp(appId));
    },
    async deleteApp(appId) {
      requireApp(appId);
      if (developmentPaymentOrders.some((order) => order.appId === appId)) {
        throw new M1ApiError("APP_IN_USE", "该应用已有用户或订单，不能删除，请先停用");
      }
      const { [appId]: _credential, ...credentials } = state.credentials;
      const { [appId]: _configs, ...configs } = state.configs;
      const { [appId]: _payChannels, ...payChannels } = state.payChannels;
      state = {
        ...state,
        apps: state.apps.filter((item) => item.id !== appId),
        credentials,
        configs,
        payChannels,
        paymentConfigs: state.paymentConfigs.map((item) => ({
          ...item,
          associatedApps: item.associatedApps.filter((app) => app.id !== appId),
        })),
      };
      persist();
    },
    async getCredential(appId) {
      requireApp(appId);
      const credential = state.credentials[appId];
      if (!credential) throw new M1ApiError("CREDENTIAL_NOT_FOUND", "未找到应用凭证");
      return structuredClone(credential);
    },
    async resetCredential(appId) {
      const app = requireApp(appId);
      const current = state.credentials[appId];
      if (!current) throw new M1ApiError("CREDENTIAL_NOT_FOUND", "未找到应用凭证");
      const now = new Date().toISOString();
      const credential = { appKey: current.appKey, appSecret: createOpaqueValue("ys", 32), revealedAt: now };
      state = {
        ...state,
        credentials: { ...state.credentials, [appId]: { ...current, secretLastResetAt: now } },
        apps: state.apps.map((item) => item.id === app.id ? { ...item, updatedAt: now } : item),
      };
      persist();
      return credential;
    },
    async listConfigs(appId) {
      requireApp(appId);
      return structuredClone(state.configs[appId] ?? []);
    },
    async upsertConfig(appId, input) {
      requireApp(appId);
      const parsed = appConfigSchema.safeParse(input);
      if (!parsed.success) throw new M1ApiError("VALIDATION_ERROR", "请检查配置字段");
      validateConfigValue(input);
      if (input.key === "DRIVING_PROFILE_OPTIONS" && (!input.isPublic || input.valueType !== "json" || !drivingProfileOptionsSchema.safeParse(JSON.parse(input.value)).success)) {
        throw new M1ApiError("DRIVING_PROFILE_OPTIONS_INVALID", "驾驶资料选项必须使用公开的中维双语 JSON 配置", { value: "请检查中文、维吾尔语标签和选项结构" });
      }
      const current = state.configs[appId] ?? [];
      const exists = current.some((item) => item.key === input.key);
      const next = exists ? current.map((item) => item.key === input.key ? parsed.data : item) : [...current, parsed.data];
      const updatedAt = new Date().toISOString();
      state = {
        ...state,
        configs: { ...state.configs, [appId]: next },
        apps: state.apps.map((item) => item.id === appId ? { ...item, configCount: next.length, updatedAt } : item),
      };
      persist();
      return structuredClone(parsed.data);
    },
    async deleteConfig(appId, key) {
      requireApp(appId);
      const current = state.configs[appId] ?? [];
      const next = current.filter((item) => item.key !== key);
      const updatedAt = new Date().toISOString();
      state = {
        ...state,
        configs: { ...state.configs, [appId]: next },
        apps: state.apps.map((item) => item.id === appId ? { ...item, configCount: next.length, updatedAt } : item),
      };
      persist();
    },
    async listPayChannels(appId) {
      requireApp(appId);
      return structuredClone(state.payChannels[appId] ?? []);
    },
    async upsertPayChannel(appId, input) {
      requireApp(appId);
      const parsed = upsertPayChannelInputSchema.safeParse(input);
      if (!parsed.success) throw new M1ApiError("VALIDATION_ERROR", "请检查支付渠道字段");
      const value: PayChannelConfig = { ...parsed.data, updatedAt: new Date().toISOString() };
      const current = state.payChannels[appId] ?? [];
      const exists = current.some((item) => item.channel === value.channel);
      const next = exists ? current.map((item) => item.channel === value.channel ? value : item) : [...current, value];
      state = {
        ...state,
        payChannels: { ...state.payChannels, [appId]: next },
        apps: state.apps.map((item) => item.id === appId ? { ...item, payChannels: next.map((channel) => channel.channel), updatedAt: value.updatedAt } : item),
      };
      persist();
      return structuredClone(value);
    },
    async deletePayChannel(appId, channel) {
      requireApp(appId);
      const next = (state.payChannels[appId] ?? []).filter((item) => item.channel !== channel);
      const updatedAt = new Date().toISOString();
      state = {
        ...state,
        payChannels: { ...state.payChannels, [appId]: next },
        apps: state.apps.map((item) => item.id === appId ? { ...item, payChannels: next.map((value) => value.channel), updatedAt } : item),
      };
      persist();
    },
    async listPaymentOrders(input) {
      const keyword = input.keyword.trim().toLocaleLowerCase();
      const base = developmentPaymentOrders.filter((order) => {
        const matchesApp = !input.appId || order.appId === input.appId;
        const matchesChannel = input.channel === "all" || order.channel === input.channel;
        const matchesKeyword = !keyword || `${order.id} ${order.appName} ${order.appCode} ${order.userName} ${order.userPublicId} ${order.description} ${order.providerOrderId ?? ""}`.toLocaleLowerCase().includes(keyword);
        return matchesApp && matchesChannel && matchesKeyword;
      });
      const filtered = base.filter((order) => input.status === "all" || order.status === input.status);
      const offset = (input.page - 1) * input.pageSize;
      return structuredClone({
        items: filtered.slice(offset, offset + input.pageSize),
        total: filtered.length,
        summary: {
          total: base.length,
          processing: base.filter((order) => order.status === "pending" || order.status === "processing").length,
          succeeded: base.filter((order) => order.status === "succeeded").length,
          failed: base.filter((order) => order.status === "failed").length,
        },
      });
    },
    async getPaymentOrder(orderId) {
      const order = developmentPaymentOrders.find((item) => item.id === orderId);
      if (!order) throw new M1ApiError("PAYMENT_ORDER_NOT_FOUND", "支付订单不存在");
      return structuredClone(developmentPaymentDetail(order));
    },
    async syncPaymentOrder(orderId) {
      const order = developmentPaymentOrders.find((item) => item.id === orderId);
      if (!order) throw new M1ApiError("PAYMENT_ORDER_NOT_FOUND", "支付订单不存在");
      if (order.channel !== "wxpay") throw new M1ApiError("ORDER_NOT_WECHAT", "订单不是微信支付订单");
    },
    async syncPaymentRefund(orderId, refundId) {
      const order = developmentPaymentOrders.find((item) => item.id === orderId);
      if (!order) throw new M1ApiError("PAYMENT_ORDER_NOT_FOUND", "支付订单不存在");
      const refund = developmentPaymentDetail(order).refunds.find((item) => item.id === refundId || item.outRefundNo === refundId);
      if (!refund) throw new M1ApiError("REFUND_NOT_FOUND", "退款单不存在");
    },
    async getPaymentReconciliation(input) {
      const grossByChannel = new Map<string, { orderCount: number; grossAmountMinor: number; refundedAmountMinor: number }>();
      developmentPaymentOrders.filter((order) => order.createdAt.slice(0, 10) >= input.dateFrom && order.createdAt.slice(0, 10) <= input.dateTo && (input.channel === "all" || order.channel === input.channel) && (order.status === "succeeded" || order.status === "refunded")).forEach((order) => {
        const current = grossByChannel.get(order.channel) ?? { orderCount: 0, grossAmountMinor: 0, refundedAmountMinor: 0 };
        current.orderCount += 1;
        current.grossAmountMinor += order.amountMinor;
        current.refundedAmountMinor += developmentPaymentDetail(order).refunds.filter((refund) => refund.status === "processing" || refund.status === "succeeded").reduce((sum, refund) => sum + refund.amountMinor, 0);
        grossByChannel.set(order.channel, current);
      });
      const rows = [...grossByChannel.entries()].map(([channel, value]) => {
        const statement = statementBatches.find((item) => item.channel === channel && item.dateFrom === input.dateFrom && item.dateTo === input.dateTo);
        return { channel: channel as "wxpay" | "alipay" | "apple", ...value, netAmountMinor: Math.max(0, value.grossAmountMinor - value.refundedAmountMinor), mismatchCount: statement?.mismatchCount ?? 0 };
      });
      const summary = rows.reduce((total, row) => ({ orderCount: total.orderCount + row.orderCount, grossAmountMinor: total.grossAmountMinor + row.grossAmountMinor, refundedAmountMinor: total.refundedAmountMinor + row.refundedAmountMinor, netAmountMinor: total.netAmountMinor + row.netAmountMinor, mismatchCount: total.mismatchCount + row.mismatchCount }), { orderCount: 0, grossAmountMinor: 0, refundedAmountMinor: 0, netAmountMinor: 0, mismatchCount: 0 });
      return { id: `recon_${input.dateFrom}_${input.dateTo}_${input.channel}`, dateFrom: input.dateFrom, dateTo: input.dateTo, channel: input.channel, status: summary.mismatchCount ? "attention" as const : "completed" as const, generatedAt: new Date().toISOString(), summary, rows };
    },
    async getPaymentStatementWorkspace(input) {
      const differences = statementDifferences.filter((difference) => {
        const batch = statementBatches.find((item) => item.id === difference.batchId);
        return batch && batch.dateTo >= input.dateFrom && batch.dateFrom <= input.dateTo && (input.channel === "all" || batch.channel === input.channel);
      });
      const resolvedDifferences = differences.filter((difference) => difference.resolutionStatus === "resolved" || difference.resolutionStatus === "ignored").length;
      return structuredClone({
        batches: statementBatches.filter((batch) => batch.dateTo >= input.dateFrom && batch.dateFrom <= input.dateTo && (input.channel === "all" || batch.channel === input.channel)),
        differences,
        jobs: statementJobs.filter((job) => job.statementDate >= input.dateFrom && job.statementDate <= input.dateTo && (input.channel === "all" || job.channel === input.channel)),
        audits: differenceAudits.filter((audit) => differences.some((difference) => difference.id === audit.differenceId)),
        metrics: {
          totalDifferences: differences.length,
          resolvedDifferences,
          overdueDifferences: differences.filter((difference) => !["resolved", "ignored"].includes(difference.resolutionStatus) && Date.now() - new Date(difference.createdAt).getTime() > 86_400_000).length,
          completionRate: differences.length ? Number((resolvedDifferences * 100 / differences.length).toFixed(1)) : 100,
        },
      });
    },
    async importPaymentStatement(input) {
      const parsed = paymentStatementImportInputSchema.parse(input);
      const existing = statementBatches.find((batch) => batch.checksum === parsed.checksum);
      if (existing) return structuredClone(existing);
      const batchId = `stm_${Date.now()}`;
      statementRows.set(batchId, structuredClone(parsed.rows));
      return structuredClone(reconcileStatement(parsed, batchId, new Date().toISOString()));
    },
    async rerunPaymentStatement(batchId) {
      const current = statementBatches.find((batch) => batch.id === batchId);
      if (!current) throw new M1ApiError("PAYMENT_STATEMENT_NOT_FOUND", "渠道账单批次不存在");
      const rows = statementRows.get(batchId);
      if (!rows) {
        const updated = { ...current, completedAt: new Date().toISOString() };
        statementBatches = statementBatches.map((batch) => batch.id === batchId ? updated : batch);
        return structuredClone(updated);
      }
      return structuredClone(reconcileStatement({
        channel: current.channel,
        fileName: current.fileName,
        checksum: current.checksum,
        dateFrom: current.dateFrom,
        dateTo: current.dateTo,
        rows,
      }, batchId, current.createdAt));
    },
    async triggerPaymentStatementJob(input) {
      const value = triggerPaymentStatementJobInputSchema.parse(input);
      const config = state.paymentConfigs.find((item) => item.id === value.configId);
      if (!config) throw new M1ApiError("PAYMENT_CONFIG_NOT_FOUND", "支付配置不存在");
      const billScenario = config.channel === "wxpay" ? "wx_bill" : config.channel === "alipay" ? "alipay_bill" : null;
      if (!config.enabled || !billScenario || !config.scenarios.includes(billScenario)) throw new M1ApiError("PAYMENT_STATEMENT_CONFIG_NOT_READY", "支付配置未启用账单下载场景");
      const now = new Date().toISOString();
      const existing = statementJobs.find((job) => job.configId === value.configId && job.statementDate === value.statementDate);
      const job: PaymentStatementJob = existing ? { ...existing, status: "queued", errorMessage: null, completedAt: null, createdAt: now, createdBy: "local-admin" } : {
        id: `stj_${Date.now()}`, configId: config.id, configName: config.name, channel: config.channel as "wxpay" | "alipay",
        statementDate: value.statementDate, trigger: "manual", status: "queued", attemptCount: 0, batchId: null,
        errorMessage: null, startedAt: null, completedAt: null, createdBy: "local-admin", createdAt: now,
      };
      statementJobs = [job, ...statementJobs.filter((item) => item.id !== job.id)];
      return structuredClone(job);
    },
    async updatePaymentDifference(differenceId, input) {
      const value = updatePaymentDifferenceInputSchema.parse(input);
      const current = statementDifferences.find((difference) => difference.id === differenceId);
      if (!current) throw new M1ApiError("PAYMENT_DIFFERENCE_NOT_FOUND", "对账差异不存在或已经失效");
      const now = new Date().toISOString();
      const updated: PaymentReconciliationDifference = {
        ...current,
        resolutionStatus: value.resolutionStatus,
        assignee: value.assignee,
        resolutionAction: value.resolutionAction,
        resolutionNote: value.note || null,
        resolvedAt: value.resolutionStatus === "resolved" || value.resolutionStatus === "ignored" ? now : null,
        updatedAt: now,
      };
      statementDifferences = statementDifferences.map((difference) => difference.id === differenceId ? updated : difference);
      differenceAudits = [{
        id: `dra_${Date.now()}`, differenceId, action: current.resolutionStatus !== updated.resolutionStatus ? "status_changed" : current.assignee !== updated.assignee ? "assigned" : "noted",
        fromStatus: current.resolutionStatus, toStatus: updated.resolutionStatus, assignee: updated.assignee,
        resolutionAction: updated.resolutionAction, note: updated.resolutionNote, operatorId: "local-admin", createdAt: now,
      }, ...differenceAudits];
      return structuredClone(updated);
    },
    async listPaymentWebhookEvents(input) {
      const filtered = webhookEvents.filter((event) => (
        event.createdAt.slice(0, 10) >= input.dateFrom
        && event.createdAt.slice(0, 10) <= input.dateTo
        && (input.channel === "all" || event.channel === input.channel)
        && (input.eventType === "all" || event.eventType === input.eventType)
        && (input.status === "all" || event.processingStatus === input.status)
        && (!input.keyword || [event.eventKey, event.outTradeNo, event.outRefundNo, event.providerOrderId].some((value) => value?.toLowerCase().includes(input.keyword.toLowerCase())))
      ));
      const start = (input.page - 1) * input.pageSize;
      return structuredClone({
        items: filtered.slice(start, start + input.pageSize),
        total: filtered.length,
        summary: {
          total: filtered.length,
          processed: filtered.filter((event) => event.processingStatus === "processed").length,
          attention: filtered.filter((event) => ["received", "rejected", "failed"].includes(event.processingStatus)).length,
          rejected: filtered.filter((event) => event.processingStatus === "rejected").length,
          duplicates: filtered.reduce((total, event) => total + event.duplicateCount, 0),
        },
      });
    },
    async getPaymentWebhookEvent(eventId) {
      const event = webhookEvents.find((item) => item.id === eventId);
      if (!event) throw new M1ApiError("PAYMENT_WEBHOOK_EVENT_NOT_FOUND", "支付通知事件不存在");
      return structuredClone({ event, audits: webhookAudits.filter((audit) => audit.eventId === eventId) });
    },
    async retryPaymentWebhookEvent(eventId) {
      const event = webhookEvents.find((item) => item.id === eventId);
      if (!event) throw new M1ApiError("PAYMENT_WEBHOOK_EVENT_NOT_FOUND", "支付通知事件不存在");
      if (event.signatureStatus !== "verified") throw new M1ApiError("PAYMENT_WEBHOOK_SIGNATURE_REJECTED", "验签失败的通知不能重新处理");
      if (event.processingStatus === "processed" || event.processingStatus === "ignored") return structuredClone(event);
      const now = new Date().toISOString();
      const updated: PaymentWebhookEvent = { ...event, source: "retry", processingStatus: "processed", failureCode: null, failureReason: null, attemptCount: event.attemptCount + 1, updatedAt: now, processedAt: now };
      webhookEvents = webhookEvents.map((item) => item.id === eventId ? updated : item);
      webhookAudits = [...webhookAudits, { id: `pwa_${Date.now()}_retry`, eventId, action: "retried", fromStatus: event.processingStatus, toStatus: "received", reason: "管理员重新处理通知", operatorId: "local-admin", createdAt: now }, { id: `pwa_${Date.now()}_processed`, eventId, action: "processed", fromStatus: "received", toStatus: "processed", reason: null, operatorId: "local-admin", createdAt: now }];
      return structuredClone(updated);
    },
    async createPaymentRefund(orderId, input) {
      const order = developmentPaymentOrders.find((item) => item.id === orderId);
      if (!order) throw new M1ApiError("PAYMENT_ORDER_NOT_FOUND", "支付订单不存在");
      if (order.channel !== "wxpay") throw new M1ApiError("REFUND_NOT_WECHAT", "当前订单不是微信支付订单");
      if (order.status !== "succeeded") throw new M1ApiError("REFUND_ORDER_NOT_PAID", "只有已支付订单可以退款");
      const existing = developmentPaymentDetail(order).refunds;
      const refunded = existing.filter((item) => item.status === "processing" || item.status === "succeeded").reduce((sum, item) => sum + item.amountMinor, 0);
      if (input.amountMinor + refunded > order.amountMinor) throw new M1ApiError("REFUND_AMOUNT_EXCEEDS_ORDER", "退款金额超过可退余额");
      const now = new Date().toISOString();
      const value: PaymentRefund = {
        id: `rfd_${Date.now()}`, orderId, appId: order.appId, idempotencyKey: input.idempotencyKey,
        outRefundNo: `rfd_out_${Date.now()}`, providerRefundId: `wx_refund_${Date.now()}`, amountMinor: input.amountMinor,
        currency: order.currency, reason: input.reason, status: "succeeded", failureReason: null, version: 2,
        createdAt: now, updatedAt: now, completedAt: now,
      };
      developmentPaymentRefunds[orderId] = [...(developmentPaymentRefunds[orderId] ?? []), value];
      order.status = "refunded";
      return structuredClone(value);
    },
    async listPaymentConfigs() {
      return structuredClone(state.paymentConfigs);
    },
    async upsertPaymentConfig(id, input) {
      const now = new Date().toISOString();
      const current = id ? state.paymentConfigs.find((item) => item.id === id) : undefined;
      const { credentialValues = {}, ...config } = input;
      const credentialStatus = { ...(current?.credentialStatus ?? {}), ...Object.fromEntries(Object.keys(credentialValues).map((key) => [key, true])) };
      const value: PaymentConfig = { ...config, credentialStatus, id: id ?? `payment-config-${Date.now()}`, updatedAt: now, associatedApps: current?.associatedApps ?? [] };
      let nextChannels = state.payChannels;
      let nextApps = state.apps;
      if (current) {
        for (const app of current.associatedApps) {
          const channels = (nextChannels[app.id] ?? []).filter((item) => item.channel !== current.channel);
          const synced: PayChannelConfig = { channel: value.channel, enabled: value.enabled, accountId: value.accountId, secretRef: value.secretRef, description: `${value.name} · ${value.description}`.slice(0, 200), updatedAt: now };
          const next = channels.some((item) => item.channel === value.channel) ? channels.map((item) => item.channel === value.channel ? synced : item) : [...channels, synced];
          nextChannels = { ...nextChannels, [app.id]: next };
          nextApps = nextApps.map((item) => item.id === app.id ? { ...item, payChannels: next.map((channel) => channel.channel), updatedAt: now } : item);
        }
      }
      state = { ...state, payChannels: nextChannels, apps: nextApps, paymentConfigs: current ? state.paymentConfigs.map((item) => item.id === id ? value : item) : [value, ...state.paymentConfigs] };
      persist();
      return structuredClone(value);
    },
    async deletePaymentConfig(id) {
      const value = state.paymentConfigs.find((item) => item.id === id);
      if (!value) throw new M1ApiError("PAYMENT_CONFIG_NOT_FOUND", "支付配置不存在");
      if (value.associatedApps.length) throw new M1ApiError("PAYMENT_CONFIG_IN_USE", "支付配置仍有关联应用，不能删除");
      state = { ...state, paymentConfigs: state.paymentConfigs.filter((item) => item.id !== id) };
      persist();
    },
    async linkPaymentConfig(appId, configId) {
      const app = requireApp(appId);
      const config = state.paymentConfigs.find((item) => item.id === configId);
      if (!config) throw new M1ApiError("PAYMENT_CONFIG_NOT_FOUND", "支付配置不存在");
      if (state.paymentConfigs.some((item) => item.id !== configId && item.channel === config.channel && item.associatedApps.some((linked) => linked.id === appId))) {
        throw new M1ApiError("PAYMENT_CONFIG_CHANNEL_CONFLICT", "同一应用不能关联多个相同支付渠道配置");
      }
      const value: PayChannelConfig = { channel: config.channel, enabled: config.enabled, accountId: config.accountId, secretRef: config.secretRef, description: `${config.name} · ${config.description}`.slice(0, 200), updatedAt: new Date().toISOString() };
      const current = state.payChannels[appId] ?? [];
      const channels = current.some((item) => item.channel === config.channel) ? current.map((item) => item.channel === config.channel ? value : item) : [...current, value];
      state = { ...state, payChannels: { ...state.payChannels, [appId]: channels }, apps: state.apps.map((item) => item.id === appId ? { ...item, payChannels: channels.map((channel) => channel.channel) } : item), paymentConfigs: state.paymentConfigs.map((item) => item.id === configId && !item.associatedApps.some((linked) => linked.id === appId) ? { ...item, associatedApps: [...item.associatedApps, { id: app.id, name: app.name, code: app.code }] } : item) };
      persist();
    },
    async unlinkPaymentConfig(appId, configId) {
      requireApp(appId);
      const config = state.paymentConfigs.find((item) => item.id === configId);
      if (!config || !config.associatedApps.some((app) => app.id === appId)) throw new M1ApiError("PAYMENT_CONFIG_LINK_NOT_FOUND", "支付配置关联不存在");
      const channels = (state.payChannels[appId] ?? []).filter((item) => item.channel !== config.channel);
      state = { ...state, payChannels: { ...state.payChannels, [appId]: channels }, apps: state.apps.map((item) => item.id === appId ? { ...item, payChannels: channels.map((channel) => channel.channel) } : item), paymentConfigs: state.paymentConfigs.map((item) => item.id === configId ? { ...item, associatedApps: item.associatedApps.filter((app) => app.id !== appId) } : item) };
      persist();
    },
  };
}
